import React, {useState, useCallback, useEffect} from 'react';
import produce from 'immer';
import {useTranslation} from 'react-i18next';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModal from '../../../components/core/SMCModal';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import {SMCTabsContainer, SMCTab} from '../../../components/core/SMCTabs';
import PreviewTab from '../TemplateEditors/TemplateTabs/PreviewTab';
import EditTab from '../TemplateEditors/TemplateTabs/EditTab';
import {setProperty} from '../../../utils/utils';
import AdministratorsTab from '../TemplateEditors/TemplateTabs/AdministratorsTab';
import UsersTab from '../TemplateEditors/TemplateTabs/UsersTab';
import DetailsTab from './DetailsTab';
import {AlertSchema} from '../../../api/DBSchema/Alert';
import {useSMCSnackbar} from '../../../utils/hooks';
import {AlertDefaultBody, AlertDefaultSubject} from './AlertInitialValues';
import Api from '../../../api/SMPFetchHelper';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

function AlertEditorModal({
    isOpen,
    onRequestClose,
    onError,
    onSuccess,
    defaultObject,
    editShortBody,
    isGlobal,
    hasAdministratorsTab=true
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const enqueueMiddleErrorSnackbar = useCallback(message => {
        enqueueErrorSnackbar(message, {
            anchorOrigin: {
                horizontal: 'center',
                vertical: 'bottom'
            }
        });
    }, [enqueueErrorSnackbar]);

    const [alertObject, setAlertObject] = useState(defaultObject);
    const [alertName, setAlertName] = useState('');
    const [pageLoaded, setPageLoaded] = useState(false);
    const [alertLogIds, setAlertLogIds] = useState([]);
    const [selectedTab, setSelectedTab] = useState('preview');

    const templateObject = alertObject ? alertObject[AlertSchema.data] : null;
    
    const setAlertObjectValue = useCallback((paramPath, newValue) => {
        setAlertObject(alertObject => produce(alertObject, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);
    const setTemplateObjectValue = useCallback((paramPath, newValue) => {
        const paramPathArray = Array.isArray(paramPath) ? paramPath : [paramPath];
        setAlertObject(alertObject => produce(alertObject, draft => {
            setProperty(draft, [AlertSchema.data, ...paramPathArray], newValue);
        }));
    }, []);

    const onFinish = useCallback(() => {
        // Validate
        if (!alertObject[AlertSchema.name]) {
            enqueueMiddleErrorSnackbar(t('com.sofaware.schema.ATTRIBUTE_REQUIRED_VALUE_MISSING', [t('SMP.NAME')]));
            return;
        }
        if (!alertObject[AlertSchema.threshold] || alertObject[AlertSchema.threshold] < 0) {
            enqueueMiddleErrorSnackbar(t('com.sofaware.db.SETTINGS_ALERT_THRESHOLD_INVALID'));
            return;
        }
        if (!alertObject[AlertSchema.timeInterval] || alertObject[AlertSchema.timeInterval] < 0) {
            enqueueMiddleErrorSnackbar(t('com.sofaware.db.SETTINGS_ALERT_TIME_INTERVAL_INVALID'));
            return;
        }
        if (!alertObject[AlertSchema.data][AlertSchema.dataSchema.messageData][AlertSchema.dataSchema.messageDataSchema.body]) {
            enqueueMiddleErrorSnackbar(t('com.sofaware.db.SETTINGS_NOTIFICATIONS_EMPTY_BODY'))
            return;            
        }
        if (!alertObject[AlertSchema.data][AlertSchema.dataSchema.messageData][AlertSchema.dataSchema.messageDataSchema.subject]) {
            enqueueMiddleErrorSnackbar(t('com.sofaware.db.SETTINGS_NOTIFICATIONS_EMPTY_SUBJECT'));
            return;            
        }
        onSuccess(alertObject);
    }, [onSuccess, alertObject, enqueueMiddleErrorSnackbar, t]);

    const handleSelectTab = useCallback((tabName) => {
        setSelectedTab(tabName);
    }, []);
    
    const handleDefaultButtonClick = useCallback(() => {
        if (window.confirm(t('SMC_MESSEGE_DEFAULT_QUES'))) {
            const messageData = {
                [AlertSchema.dataSchema.messageDataSchema.subject]: AlertDefaultSubject,
                [AlertSchema.dataSchema.messageDataSchema.bcc]: '',
                [AlertSchema.dataSchema.messageDataSchema.body]: AlertDefaultBody,
            };
                setTemplateObjectValue([AlertSchema.dataSchema.messageData], messageData);
        }
    }, [setTemplateObjectValue, t]);

    useEffect(() => {
        if (isOpen) {
            if (defaultObject) {
                setAlertObject(defaultObject);
                setAlertName(defaultObject?.[AlertSchema.name]);
            } else {
                const defaultAlertObject = {
                    [AlertSchema.name]: '',
                    [AlertSchema.description]: '',
                    [AlertSchema.logId]: null,
                    [AlertSchema.severity]: AlertSchema.severityValues.info,
                    [AlertSchema.filter]: '',
                    [AlertSchema.isAtLeast]: true,
                    [AlertSchema.threshold]: 100,
                    [AlertSchema.isSameSource]: true,
                    [AlertSchema.timeInterval]: 60,
                    [AlertSchema.isSendMail]: true,
                    [AlertSchema.data]: {
                        [AlertSchema.dataSchema.enabled]: true,
                        [AlertSchema.dataSchema.admins]: [],
                        [AlertSchema.dataSchema.users]: [],
                        [AlertSchema.dataSchema.messageData]: {
                            [AlertSchema.dataSchema.messageDataSchema.subject]: AlertDefaultSubject,
                            [AlertSchema.dataSchema.messageDataSchema.bcc]: '',
                            [AlertSchema.dataSchema.messageDataSchema.body]: AlertDefaultBody,
                        }
                    }
                }
                setAlertObject(defaultAlertObject);
                setAlertName('');
            }
            // If not loaded alertLogIds already, load them
            if (!alertLogIds || !alertLogIds.length) {
                setPageLoaded(false);
                Api.getAlertLogIds()
                    .then(resp => {
                        setAlertLogIds(resp.data.data.logIDList);
                    })
                    .catch(err => {
                        
                    })
                    .finally(() => {
                        setPageLoaded(true);
                    })
            }
        }
    }, [isOpen, defaultObject, alertLogIds]);

    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={750}
            height={590}
            title={t('SMC_NOTIFY_ALERT_TITLE')}
            loading={!pageLoaded}
        >
            <SMCModalBody lineHeight="24px">
                <SMCTabsContainer tabOnClick={handleSelectTab} style={{ padding: '10px' }}>
                    <SMCTab title={t('SMC_NOTIFY_ALERT_TAB0')} id="details">
                        <DetailsTab
                            logIdList={alertLogIds}
                            alertObject={alertObject}
                            setAlertObjectValue={setAlertObjectValue}
                        />
                    </SMCTab>
                    <SMCTab title={t('SMC_MESSEGE_TAB0')} id="preview">
                        <PreviewTab
                            templateObject={templateObject}
                            previewShortBody={editShortBody}
                            templateName={alertName || ""}
                            templateType="alert"
                            isGlobal={isGlobal}
                        />
                    </SMCTab>
                    <SMCTab title={t('SMC_MESSEGE_TAB1')} id="edit">
                        <EditTab
                            templateObject={templateObject}
                            editShortBody={editShortBody}
                            setTemplateObjectValue={setTemplateObjectValue}
                        />
                    </SMCTab>
                    {
                        hasAdministratorsTab && (
                            <SMCTab title={t('SMC_MESSEGE_TAB2')} id="admins">
                                <AdministratorsTab
                                    templateObject={templateObject}
                                    setTemplateObjectValue={setTemplateObjectValue}
                                />
                            </SMCTab>
                        )
                    }
                    <SMCTab title={t('SMC_MESSEGE_TAB3')} id="users">
                        <UsersTab
                            templateObject={templateObject}
                            setTemplateObjectValue={setTemplateObjectValue}
                        />
                    </SMCTab>
                </SMCTabsContainer>
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_OK')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
                {
                    selectedTab === 'edit' && (
                        <SMCButton onClick={handleDefaultButtonClick}>
                            {t('SMC_GEN_DEFAULT')}
                        </SMCButton>
                    )
                }
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default React.memo(AlertEditorModal);